import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AirportLounge, Events, Flights, Hotel, Languages, Logo, More, Rental, SampleUser, Search, Wishlist } from '../assests/ExportImageIcon'
import accountSetupSteps from '../routes/accountSetupSteps'
import { setLanguage } from '../redux/locale/localeSlice'
import { changeGoogleTranslateLanguage } from '../utils/googleTranslate'
import service from '../services/service'
import endpoints from '../services/endpoints'
import { updateUser } from '../redux/auth/authSlice'
import { resolveProfileImage, withProfileImage } from '../utils/profileImage'
import useNotificationSocket from '../hooks/useNotificationSocket'
import NotificationCenter from './notificationCenter'
import './header.css'

const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path d="M3 6h16M3 11h16M3 16h16" stroke="#1D2A4D" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M4 4l10 10M14 4L4 14" stroke="#1D2A4D" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

// No dedicated Holidays icon asset exists yet (only webp icons for the other
// nav items) — inline beach-umbrella SVG placeholder, sized to match those icons.
const HolidaysIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M12 3c4.97 0 9 3.58 9 8H3c0-4.42 4.03-8 9-8Z" stroke="#026D4D" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M12 11v8a2 2 0 0 1-2 2" stroke="#026D4D" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M12 3v2" stroke="#026D4D" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
)

const Header = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn)
  const language = useSelector((state) => state.locale.language)
  const user = useSelector((state) => state.auth.user)
  const activeStepIndex = accountSetupSteps.findIndex((step) => step.path === location.pathname)
  const isAccountSetup = activeStepIndex !== -1
  const [menuOpen, setMenuOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef(null)

  useNotificationSocket()

  // Close the mobile menu on route change, and don't let it persist stale
  // open across navigations.
  useEffect(() => {
    setMenuOpen(false)
    setMoreOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!moreOpen) return undefined
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [moreOpen])

  // Local URL so header always tracks the same source as Profile (not only Redux)
  const [headerAvatar, setHeaderAvatar] = useState(() => resolveProfileImage(user))

  useEffect(() => {
    setHeaderAvatar((prev) => resolveProfileImage(user) || prev)
  }, [user])

  useEffect(() => {
    // The account-setup wizard runs on a "pre-signup" token issued right
    // after OTP verify, before /users/user-account has an account to
    // return — fetching here always 401s ("credentials were not
    // provided") for no benefit, since the avatar UI isn't even rendered
    // during account-setup (see the isAccountSetup branch below).
    if (!isLoggedIn || isAccountSetup) {
      setHeaderAvatar('')
      return undefined
    }

    let cancelled = false
    service
      .get(endpoints.users.userAccount, {}, { silent: true })
      .then(({ data }) => {
        if (cancelled) return
        const account = data?.data
        if (!account || typeof account !== 'object') return

        const patch = withProfileImage(account)
        const nextImage = patch.profileImageUrl || resolveProfileImage(patch)
        if (nextImage) {
          setHeaderAvatar(nextImage)
        }
        // Always merge account + explicit display URL (never drop a known image)
        dispatch(updateUser(patch))
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
    // Deliberately NOT depending on location.pathname — this should fetch
    // once per login (or once account-setup finishes), not on every single
    // navigation. It used to, which re-requested /users/user-account on
    // every page change and reloaded the avatar each time (the backend
    // returns a freshly-signed S3 URL per call, so the <img src> — and its
    // key={avatarSrc} below — changed on every nav, forcing a visible
    // reload even though the picture never actually changed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isAccountSetup, dispatch])

  const avatarSrc = (isLoggedIn && (headerAvatar || resolveProfileImage(user))) || SampleUser

  return (
    <>
      <header>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="headerMain">
                <Link to={isLoggedIn ? "/home" : "/"} className="logo">
                  <img src={Logo} alt="Logo" />
                </Link>

                {!isAccountSetup && (
                  <button
                    type="button"
                    className="headerMenuToggle"
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((prev) => !prev)}
                  >
                    {menuOpen ? <CloseIcon /> : <MenuIcon />}
                  </button>
                )}

                {isAccountSetup ? (
                  <>
                    <nav className="accountSteps">
                      {accountSetupSteps.map((step, index) => (
                        <React.Fragment key={step.path}>
                          {index > 0 && <span className="accountSteps__dot">&gt;</span>}
                          <span
                            className={
                              index === activeStepIndex
                                ? 'accountSteps__step accountSteps__step--active'
                                : 'accountSteps__step'
                            }
                          >
                            {step.label}
                          </span>
                        </React.Fragment>
                      ))}
                    </nav>

                    <div className="accountMeta">
                      <span className="accountMeta__count">
                        Step {activeStepIndex + 1} of {accountSetupSteps.length}
                      </span>
                      <div className="accountMeta__progress">
                        <div
                          className="accountMeta__progressFill"
                          style={{ width: `${((activeStepIndex + 1) / accountSetupSteps.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={`headerMobilePanel ${menuOpen ? 'is-open' : ''}`}>
                    <div className="nav">
                      <ul>
                        <li><Link to="/venue-list" className={location.pathname === '/venue-list' ? 'active' : ''}><img src={Hotel} alt="Venue" /> Venue</Link></li>
                        <li><Link to="/holidays" className={location.pathname === '/holidays' ? 'active' : ''}><HolidaysIcon /> Travel Packages</Link></li>
                        <li><Link to="/event-booking" className={location.pathname === '/event-booking' ? 'active' : ''}><img src={Events} alt="Events" /> Events</Link></li>
                        <li><Link to="/car-rental" className={location.pathname === '/car-rental' ? 'active' : ''}><img src={Rental} alt="Rentals" /> Rentals</Link></li>
                        <li><Link to="#"><img src={AirportLounge} alt="Airport Lounge" /> Airport Lounge</Link></li>
                        <li className={`hasDropdown ${moreOpen ? 'is-open' : ''}`} ref={moreRef}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setMoreOpen((prev) => !prev) }}>
                            <img src={More} alt="More" /> More
                          </a>
                          <ul className="moreDropdownMenu"> 
                            <li>
                              <Link to="/flight-tracking" onClick={() => setMoreOpen(false)}>
                                <img src={Flights} alt="" /> Flight Status
                              </Link>
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                    <div className="user-actions">
                      <div className="left">
                        <Link to="/wishlist" className="wishlist">
                          <img src={Wishlist} alt="Wishlist" />
                        </Link>
                        <NotificationCenter />
                      </div>
                      <p>|</p>
                      <div className='right'>
                        <div className='languages'>
                          <img src={Languages} alt="Languages" />
                          <select
                            value={language}
                            onChange={(e) => {
                              const value = e.target.value
                              dispatch(setLanguage(value))
                              changeGoogleTranslateLanguage(value)
                            }}
                            aria-label="Select language"
                          >
                            <option value="en">English</option>
                            <option value="hi">हिन्दी</option>
                            <option value="es">Español</option>
                            <option value="ar">العربية</option>
                            <option value="fr">Français</option>
                            <option value="de">Deutsch</option>
                            <option value="pt">Português</option>
                            <option value="ru">Русский</option>
                            <option value="ja">日本語</option>
                            <option value="ko">한국어</option>
                          </select>
                        </div>
                        <Link to={isLoggedIn ? '/profile' : '/'} className='sampleUser' title={isLoggedIn ? 'My profile' : 'Login'}>
                          <img
                            key={avatarSrc}
                            src={avatarSrc}
                            alt="Profile"
                            className='sampleUser__img'
                            onError={(e) => {
                              // Fall back only if remote URL fails to load
                              if (e.currentTarget.src !== SampleUser) {
                                e.currentTarget.src = SampleUser
                              }
                            }}
                          />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header
